import React, { useState, useEffect } from 'react'
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import { useCreatePipelineMutation } from 'src/services/dealpipeline-crm-service'
import { toast } from 'react-hot-toast'
import { renderLabelWithIcon } from 'src/pages/statusLabelSelect'

const PipelineCreationFlow = ({ open, onClose, labels, workspaceId, userId }) => {
  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [selectedLabels, setSelectedLabels] = useState([])
  const [isCreating, setIsCreating] = useState(false)

  const [createPipeline] = useCreatePipelineMutation()

  useEffect(() => {
    if (!open) {
      setStep(0)
      setName('')
      setSelectedLabels([])
      setIsCreating(false)
    }
  }, [open])

  const handleLabelToggle = (labelId) => {
    setSelectedLabels((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
    )
  }

  const handleSubmit = async () => {
    if (!name.trim() || selectedLabels.length === 0) return
    setIsCreating(true)

    try {
      await createPipeline({
        title: name,
        workspaceId,
        description: 'Created via UI flow',
        labels: selectedLabels,
        createdBy: userId
      }).unwrap()
      toast.success('Pipeline created successfully')
      onClose()
    } catch (err) {
      toast.error('Failed to create pipeline')
    } finally {
      setIsCreating(false)
    }
  }

  const canClose = step === 0 || selectedLabels.length > 0

  return (
    <Modal
      open={open}
      onClose={() => {
        if (canClose) onClose()
      }}
      disableEscapeKeyDown={!canClose}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: '12px',
          p: 4
        }}
      >
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>Pipeline Name</StepLabel>
          </Step>
          <Step>
            <StepLabel>Stages/Columns</StepLabel>
          </Step>
        </Stepper>

        {step === 0 && (
          <>
            <Typography variant='h6' fontWeight='700' mb={2}>
              Name your pipeline
            </Typography>
            <TextField
              fullWidth
              label='Pipeline name'
              variant='outlined'
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 4 }}
            />
            <Box display='flex' justifyContent='flex-end' gap={2}>
              <Button onClick={onClose} disabled={isCreating}>
                Cancel
              </Button>
              <Button variant='contained' disabled={!name.trim()} onClick={() => setStep(1)}>
                Next
              </Button>
            </Box>
          </>
        )}

        {step === 1 && (
          <>
            <Typography variant='h6' fontWeight='700' mb={2}>
              Select Columns
            </Typography>

            <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 3, px: 1 }}>
              {labels.map((label) => (
                <FormControlLabel
                  key={label._id}
                  control={
                    <Checkbox
                      checked={selectedLabels.includes(label._id)}
                      onChange={() => handleLabelToggle(label._id)}
                    />
                  }
                  label={renderLabelWithIcon(label)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    py: 1,
                    borderBottom: '1px solid #eee',
                    ml: 1
                  }}
                />
              ))}
            </Box>

            <Box display='flex' justifyContent='space-between' gap={2}>
              <Button
                onClick={() => {
                  if (!isCreating) setStep(0)
                }}
                disabled={isCreating}
              >
                Back
              </Button>
              <Button
                variant='contained'
                disabled={selectedLabels.length === 0 || isCreating}
                onClick={handleSubmit}
              >
                {isCreating
                  ? (
                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                    )
                  : (
                      'Create Pipeline'
                    )}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  )
}

export default PipelineCreationFlow
